import { useState, useEffect, useRef } from "react";
import { Button } from "@/projects/IPO/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/projects/IPO/components/ui/dialog";
import {
  Select as RadixSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import { CommonModal } from "@/common/components/ui";
import { AlertCircle, Plus, Minus, Loader2 } from "lucide-react";
import IPOService from "@/services/IPO/APIs/IPOService";
import PaymentService from "@/services/NPS/Stepper/APIs/PaymentService";
import { useLocation } from "react-router-dom";
import { notify } from "@/common/components/common/Notification";

interface IPOApplicationDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  ipo: any;
}

export function IPOApplicationDialog({
  isOpen,
  setIsOpen,
  ipo,
}: IPOApplicationDialogProps) {
  const [quantity, setQuantity] = useState(0);
  const [investorType, setInvestorType] = useState("individual");
  const [category, setCategory] = useState("retail");
  const [depository, setDepository] = useState("NSDL");
  const [boDpId, setBoDpId] = useState("IN");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [useCutOffPrice, setUseCutOffPrice] = useState(true);
  const [isHniConfirmOpen, setIsHniConfirmOpen] = useState(false);
  const [pendingQuantity, setPendingQuantity] = useState(0);
  const [applicantName, setApplicantName] = useState("");
  const [panCard, setPanCard] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [upiId, setUpiId] = useState("");
  const [verifiedName, setVerifiedName] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const upiInputRef = useRef<HTMLInputElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isOpen && ipo) {
      const initialQty = ipo?.minBidQuantity ?? ipo?.lotSize ?? 0;
      setQuantity(initialQty);
      const initialAmount = initialQty * (ipo?.priceRange?.max ?? 0);
      if (initialAmount > 200000) {
        setCategory("hni");
        setUseCutOffPrice(false);
      } else {
        setCategory("retail");
        setUseCutOffPrice(true);
      }
    }
  }, [isOpen, ipo]);

  const handleVerifyVPA = async () => {
    if (!upiId) {
      setError("Please enter UPI ID");

      // 🔥 Auto-focus when empty
      setTimeout(() => {
        upiInputRef.current?.focus();
      }, 50);

      return;
    }

    setIsVerifying(true);
    setError("");
    setVerifiedName("");

    try {
      const payload = { vpaAddress: upiId };
      const response = await PaymentService.validateVPA(payload);

      if (response?.vpa_holder_name) {
        setVerifiedName(response.vpa_holder_name);
      } else {
        setError("Could not verify UPI ID");

        // 🔥 Focus again when incorrect
        setTimeout(() => {
          upiInputRef.current?.focus();
        }, 50);
      }
    } catch (err) {
      console.error(err, "err");
      setError("Invalid UPI ID");

      // 🔥 Focus again when API error
      setTimeout(() => {
        upiInputRef.current?.focus();
      }, 50);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleApply = async () => {
    setIsSubmitted(true);
    if (boDpId.length !== 16) {
      notify.error(
        "Validation Error",
        "BO / DP ID must be exactly 16 characters"
      );
      return;
    }
    if (!verifiedName) {
      notify.warning("Validation Error", "Please verify your UPI ID first");
      return;
    }
    if (!agreeTerms) {
      notify.warning(
        "Terms Required",
        "Please agree to the terms and conditions"
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ipoID: ipo.id ?? 0,
        ipoUserID: location.state?.userId ?? "", // Example: Assumes userId might be in state or needs to come from auth context
        symbol: ipo.companyShortName ?? "",
        clientName: applicantName,
        depository: depository,
        upi: upiId,
        upiVerified: true, // we send true since we verified it
        pan: panCard,
        mobile: mobileNo,
        groupCode: "", // Needs proper mapping if available
        quantity: quantity,
        lotSize: ipo.lotSize ?? 0,
        price: useCutOffPrice
          ? (ipo.priceRange?.max ?? 0)
          : (ipo.priceRange?.min ?? 0), // Adjust price based on cutoff
        amount: totalAmount,
        exchange: ipo.exchange ?? "NSE",
      };

      const response = await IPOService.applyIPO(payload);

      if (
        response?.isSuccess ||
        response?.status === 200 ||
        response?.status === 201
      ) {
        notify.success(
          "Success",
          response?.message ?? "Application submitted successfully!"
        );
        setIsOpen(false);
        setIsSubmitted(false);
      } else {
        notify.error(
          "Application Failed",
          response?.message ??
            "Something went wrong while submitting your application."
        );
      }
    } catch (error: any) {
      notify.error(
        "Error",
        error?.response?.data?.message ??
          "Failed to submit application. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmHni = () => {
    setCategory("hni");
    setUseCutOffPrice(false);
    if (pendingQuantity > 0) {
      setQuantity(pendingQuantity);
    }
    setIsHniConfirmOpen(false);
    setPendingQuantity(0);
  };

  const totalAmount = quantity * (ipo?.priceRange?.max ?? 0);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open);
        if (!open) {
          setIsSubmitted(false);
          setBoDpId(depository === "NSDL" ? "IN" : "");
        }
      }}
    >
      <DialogContent className="max-h-auto max-w-xl overflow-y-auto bg-white">
        <DialogHeader className="-m-6 mb-4 rounded-t-lg bg-gradient-to-r from-[#0c4a6e] via-[#075985] to-[#66A0CD] p-6 text-white">
          <div className="flex items-center gap-3">
            {ipo?.logo && (
              <div className="flex h-12 w-16 items-center justify-center overflow-hidden rounded bg-white p-1">
                <img
                  src={ipo.logo}
                  alt={ipo.companyName}
                  className="h-full w-full object-contain"
                />
              </div>
            )}
            <DialogTitle className="text-xl">{ipo?.companyName}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-3 px-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-sans">Investor Type</Label>
              <RadixSelect
                value={investorType}
                onValueChange={setInvestorType}
                disabled
              >
                <SelectTrigger className="disabled:bg-slate-100 disabled:opacity-70">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">
                    Individual Investor
                  </SelectItem>
                  <SelectItem value="huf">Shareholder</SelectItem>
                </SelectContent>
              </RadixSelect>
            </div>
            <div className="space-y-2">
              <Label className="font-sans">Applicant Name</Label>
              <Input
                placeholder="Enter name"
                value={applicantName}
                onChange={e => setApplicantName(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>PAN Card</Label>
              <Input
                placeholder="Enter PAN"
                value={panCard}
                onChange={e => setPanCard(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Mobile No</Label>
              <Input
                placeholder="Enter mobile"
                value={mobileNo}
                onChange={e => setMobileNo(e.target.value)}
              />
            </div>
          </div>

          <div className="gap-4">
            <div className="space-y-2">
              <Label>UPI ID</Label>
              <div className="flex items-center gap-3">
                <Input
                  ref={upiInputRef}
                  placeholder="Enter UPI ID (e.g. maheshv@oksbi)"
                  value={upiId}
                  onChange={e => {
                    setUpiId(e.target.value);
                    setVerifiedName("");
                    setError("");
                  }}
                  className="w-full"
                />
                <Button
                  onClick={handleVerifyVPA}
                  className="finease-btn min-w-[100px]"
                  disabled={isVerifying || !upiId}
                >
                  {isVerifying ? "Verifying..." : "Verify"}
                </Button>
              </div>
              {verifiedName && (
                <div className="pop-in mt-2 flex items-center gap-2">
                  <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#66A0CD] text-xs text-white">
                    ✓
                  </span>
                  <span className="font-sans text-sm font-semibold text-[#66A0CD]">
                    {verifiedName}
                  </span>
                </div>
              )}
              {error && (
                <div className="error-shake mt-2 text-sm font-semibold text-red-500">
                  {error}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="mt-3 space-y-2">
              <Label>Depository</Label>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="nsdl"
                    name="depository"
                    value="NSDL"
                    checked={depository === "NSDL"}
                    onChange={e => {
                      setDepository(e.target.value);
                      setBoDpId("IN");
                    }}
                    className="h-4 w-4 cursor-pointer accent-[#0c4a6e]"
                  />
                  <Label htmlFor="nsdl" className="cursor-pointer">
                    NSDL
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    id="cdsl"
                    name="depository"
                    value="CDSL"
                    checked={depository === "CDSL"}
                    onChange={e => {
                      setDepository(e.target.value);
                      setBoDpId("");
                    }}
                    className="h-4 w-4 cursor-pointer accent-[#0c4a6e]"
                  />
                  <Label htmlFor="cdsl" className="cursor-pointer">
                    CDSL
                  </Label>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label
                className={
                  isSubmitted && boDpId.length !== 16 ? "text-red-500" : ""
                }
              >
                BO / DP ID
              </Label>
              <Input
                placeholder={
                  depository === "NSDL" ? "IN -" : "Enter BO / DP ID"
                }
                value={boDpId}
                onChange={e => {
                  let val = e.target.value.toUpperCase();
                  if (depository === "NSDL" && !val.startsWith("IN")) {
                    val = "IN" + val.replace(/^(I|N|IN)?/, "");
                  }
                  if (val.length <= 16) {
                    setBoDpId(val);
                  }
                }}
                className={
                  isSubmitted && boDpId.length !== 16
                    ? "border-red-500 focus-visible:ring-red-500"
                    : ""
                }
              />
              {isSubmitted && boDpId.length !== 16 && (
                <p className="text-xs font-medium text-red-500">
                  BO / DP ID must be exactly 16 characters
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 items-end gap-4">
            <div className="flex w-full gap-2">
              <Button
                variant={category === "retail" ? "default" : "outline"}
                onClick={() => {
                  setCategory("retail");
                  setUseCutOffPrice(true);
                }}
                className={
                  category === "retail"
                    ? "finease-btn w-full flex-1"
                    : "w-full flex-1"
                }
              >
                {"<"}₹2L
              </Button>
              <Button
                variant={category === "hni" ? "default" : "outline"}
                onClick={() => setIsHniConfirmOpen(true)}
                className={
                  category === "hni"
                    ? "finease-btn w-full flex-1"
                    : "w-full flex-1"
                }
              >
                {">"} ₹2L (HNI)
              </Button>
            </div>
          </div>

          <div className="rounded-xl border bg-muted/50 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">
                  Quantity (Min: {ipo?.minBidQuantity})
                </Label>
                <div className="mt-1 flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setQuantity(
                        Math.max(ipo?.minBidQuantity, quantity - ipo?.lotSize)
                      )
                    }
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    min={ipo?.minBidQuantity}
                    step={ipo?.lotSize}
                    value={quantity}
                    onChange={e => setQuantity(Number(e.target.value))}
                    onBlur={e => {
                      const val = Number(e.target.value);
                      if (val < (ipo?.minBidQuantity ?? 0)) {
                        setQuantity(ipo?.minBidQuantity ?? ipo?.lotSize);
                      }
                    }}
                    className="text-center"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const nextQuantity = quantity + ipo?.lotSize;
                      const nextAmount = nextQuantity * ipo?.priceRange?.max;
                      if (nextAmount > 200000 && category === "retail") {
                        setPendingQuantity(nextQuantity);
                        setIsHniConfirmOpen(true);
                      } else {
                        setQuantity(nextQuantity);
                      }
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {Math.ceil(quantity / ipo?.lotSize)} Lot(s)
                </p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Price</Label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cutoff"
                    checked={useCutOffPrice}
                    onChange={e => {
                      if (category === "hni") {
                        notify.error(
                          "Info",
                          "Cut-off price is not available for HNI category"
                        );
                        return;
                      }
                      setUseCutOffPrice(e.target.checked);
                    }}
                    className="h-4 w-4 accent-[#0c4a6e]"
                  />
                  <Label htmlFor="cutoff" className="cursor-pointer text-xs">
                    Cut Off Price
                  </Label>
                </div>
                <Input value={ipo?.priceRange?.max} disabled className="mt-1" />
                <p className="mt-1 text-xs text-muted-foreground">
                  ₹{ipo?.priceRange?.min} - ₹{ipo?.priceRange?.max}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-[#0c4a6e]/20 bg-gradient-to-r from-[#0c4a6e]/10 to-[#66A0CD]/10 px-5 py-4">
            <span className="font-medium text-secondary">Amount Payable</span>
            <span className="text-2xl font-bold text-[#0c4a6e]">
              ₹{totalAmount.toLocaleString()}
            </span>
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="terms"
              checked={agreeTerms}
              onChange={e => setAgreeTerms(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[#0c4a6e]"
            />
            <Label
              htmlFor="terms"
              className="cursor-pointer text-sm text-muted-foreground"
            >
              I agree to the terms and condition
            </Label>
          </div>

          <Button
            className="finease-btn w-full"
            onClick={handleApply}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Application"
            )}
          </Button>

          <CommonModal
            isOpen={isHniConfirmOpen}
            onClose={() => {
              setIsHniConfirmOpen(false);
              setPendingQuantity(0);
            }}
            width="w-full sm:w-[450px]"
            height="h-[auto]"
            padding="p-6"
          >
            <div className="space-y-6 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                <AlertCircle className="h-10 w-10 text-amber-600" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-slate-900">
                  Switch to HNI Category?
                </h3>
                <p className="text-sm text-slate-500">
                  Your application amount exceeds ₹2,00,000. To continue with
                  this amount, you need to switch to the HNI category.
                </p>
                <div className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
                  Note: Cut-off price is not available for HNI category.
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setIsHniConfirmOpen(false);
                    setPendingQuantity(0);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-secondary hover:bg-secondary/90"
                  onClick={handleConfirmHni}
                >
                  Yes, Switch
                </Button>
              </div>
            </div>
          </CommonModal>
        </div>
      </DialogContent>
    </Dialog>
  );
}
